import { useCallback, useEffect, useRef, useState } from "react";

export interface DashboardData<T> {
  data: T | undefined;
  error: string | undefined;
  /** True only on the very first load, when there is nothing to show yet. */
  loading: boolean;
  /** True while re-fetching with previous data still on screen. */
  refreshing: boolean;
  reload: () => void;
}

/**
 * Fetch-with-previous-data hook.
 *
 * On a refetch (changing the date range, say) the previous render is kept and
 * dimmed by the caller rather than being swapped for a skeleton — a dashboard
 * that blanks every tile each time you nudge a filter makes it impossible to
 * see what actually changed.
 */
export const useDashboardData = <T>(
  fetcher: () => Promise<T>,
  deps: unknown[]
): DashboardData<T> => {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [inFlight, setInFlight] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  // Guards against a slow earlier request resolving after a faster later one
  // and overwriting fresher data with stale data.
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    let cancelled = false;

    setInFlight(true);
    setError(undefined);

    fetcher()
      .then(result => {
        if (cancelled || id !== requestId.current) return;
        setData(result);
      })
      .catch((err: Error) => {
        if (cancelled || id !== requestId.current) return;
        setError(err.message);
      })
      .finally(() => {
        if (cancelled || id !== requestId.current) return;
        setInFlight(false);
      });

    return () => {
      cancelled = true;
    };
    // `fetcher` is intentionally not a dependency — callers pass an inline
    // closure, and the explicit deps list is what should drive a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadToken]);

  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  return {
    data,
    error,
    loading: inFlight && data === undefined,
    refreshing: inFlight && data !== undefined,
    reload,
  };
};
